import React, { useMemo, useState } from 'react';
import { useData } from '../../context/DataContext';
import { addAssignmentFs, addResourceFs, removeResourceFs, updateAssignmentFs, updateResourceFs } from '../../utils/firestoreStore';
import { storage } from '../../firebase';
import { ref, uploadBytesResumable, deleteObject, getDownloadURL } from 'firebase/storage';
import { CURRENT_CONTENT_SEASON, getResourceFolder } from '../../utils/seasonContent';

export default function AdminResources() {
  const { courses = [], resources = [], assignments = [] } = useData();
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [resourceForm, setResourceForm] = useState({
    title: '',
    module: '',
    type: 'pdf',
    courseId: '',
    file: null,
    downloadable: false,
    videoUrl: '',
    duration: '',
    isPaid: false,
    eventStartDate: '',
    eventEndDate: '',
    eventStartTime: '',
    eventEndTime: '',
    eventLink: '',
    mediaSource: 'upload',
    eventAudience: 'all'
  });
  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    module: '',
    dueDate: '',
    dueTime: '23:59',
    description: '',
    courseId: '',
    intake: '',
    season: CURRENT_CONTENT_SEASON
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [uploadToast, setUploadToast] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [assignmentStatus, setAssignmentStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [editingResourceId, setEditingResourceId] = useState('');
  const [editingForm, setEditingForm] = useState({
    title: '',
    module: '',
    type: 'pdf',
    courseId: '',
    downloadable: false,
    isPaid: false,
    videoUrl: '',
    duration: '',
    eventStartDate: '',
    eventEndDate: '',
    eventStartTime: '',
    eventEndTime: '',
    eventLink: '',
    mediaSource: 'upload',
    eventAudience: 'all'
  });
  const [editStatus, setEditStatus] = useState('');
  const [moveStatus, setMoveStatus] = useState('');

  const getNextFriday = () => {
    const now = new Date();
    const day = now.getDay();
    const diff = (5 - day + 7) % 7;
    const target = new Date(now);
    target.setDate(now.getDate() + diff);
    const year = target.getFullYear();
    const month = String(target.getMonth() + 1).padStart(2, '0');
    const date = String(target.getDate()).padStart(2, '0');
    return `${year}-${month}-${date}`;
  };

  const handleExtendAllAssignments = async () => {
    if (!useFirebase) return;
    if (assignments.length === 0) {
      setAssignmentStatus('No assignments found to update.');
      setTimeout(() => setAssignmentStatus(''), 3000);
      return;
    }
    setBulkUpdating(true);
    const nextFriday = getNextFriday();
    try {
      await Promise.all(
        assignments.map((assignment) =>
          updateAssignmentFs(assignment.id, { dueDate: nextFriday })
        )
      );
      setAssignmentStatus(`All assignments extended to ${nextFriday}.`);
      setTimeout(() => setAssignmentStatus(''), 3000);
    } catch (err) {
      console.error('Failed to extend assignments', err);
      setAssignmentStatus('Failed to extend assignments. Please try again.');
      setTimeout(() => setAssignmentStatus(''), 3000);
    } finally {
      setBulkUpdating(false);
    }
  };

  const sortedResources = useMemo(() => {
    const visible = resources.filter((resource) => !hiddenIds.has(resource.id));
    return [...visible].sort((a, b) => {
      const aTime = a.createdAt?.toDate?.()?.getTime?.() || 0;
      const bTime = b.createdAt?.toDate?.()?.getTime?.() || 0;
      return bTime - aTime;
    });
  }, [resources, hiddenIds]);

  const familyEconomyCourseId = useMemo(() => {
    const byTitle = courses.find(course =>
      (course.title || '').toLowerCase().includes('family economy')
    );
    if (byTitle) return byTitle.id;
    const parenting = courses.find(course =>
      (course.title || '').toLowerCase().includes('kingdom parenting')
    );
    return parenting?.id || '';
  }, [courses]);

  const toYouTubeEmbed = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    const match = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{6,})/);
    if (!match) return '';
    return `https://www.youtube-nocookie.com/embed/${match[1]}`;
  };

  const formatEventWindow = (startValue, endValue) => {
    const formatDate = (value) => {
      if (!value) return '';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleString();
    };

    const startLabel = formatDate(startValue);
    const endLabel = formatDate(endValue);

    if (startLabel && endLabel) return `Starts: ${startLabel} • Ends: ${endLabel}`;
    if (startLabel) return `Starts: ${startLabel}`;
    if (endLabel) return `Ends: ${endLabel}`;
    return '';
  };

  const splitDateTime = (value) => {
    if (!value) return { date: '', time: '' };
    const normalized = String(value).trim();
    if (normalized.includes('T')) {
      const [date, timePart] = normalized.split('T');
      return { date: date || '', time: (timePart || '').slice(0, 5) };
    }
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) return { date: normalized, time: '' };
    const date = parsed.toISOString().slice(0, 10);
    const time = `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
    return { date, time };
  };

  const combineDateTime = (date, time) => {
    if (!date) return '';
    return `${date}T${time || '00:00'}`;
  };

  const handleUpload = async () => {
    if (!useFirebase) return;
    if (!resourceForm.title.trim() || !resourceForm.module.trim()) {
      setUploadError('Please add a title and module before uploading.');
      return;
    }
    const isVideo = resourceForm.type === 'video';
    const isEvent = resourceForm.type === 'event';
    const useYoutube = isVideo || (isEvent && resourceForm.mediaSource === 'youtube');
    setUploadToast('');
    if (useYoutube) {
      if (!resourceForm.videoUrl.trim()) {
        setUploadError('Please paste a YouTube video link.');
        return;
      }
      const embed = toYouTubeEmbed(resourceForm.videoUrl);
      if (!embed) {
        setUploadError('Please enter a valid YouTube link.');
        return;
      }
    } else if (isEvent && !resourceForm.file) {
      setUploadError('Please choose an image or video poster file.');
      return;
    } else if (!resourceForm.file) {
      setUploadError('Please choose a file to upload.');
      return;
    }
    const eventStartDateTime = combineDateTime(resourceForm.eventStartDate, resourceForm.eventStartTime);
    const eventEndDateTime = combineDateTime(resourceForm.eventEndDate, resourceForm.eventEndTime);
    if (isEvent && !eventStartDateTime) {
      setUploadError('Please set the event start date and time.');
      return;
    }
    setUploadError('');
    setUploading(true);
    setUploadProgress(0);
    try {
      const courseSegment = resourceForm.courseId || 'general';
      let storagePath = null;
      let fileName = null;
      let size = null;
      let embedUrl = null;
      let mimeType = null;
      let downloadUrl = null;

      if (!useYoutube) {
        storagePath = `courses/${courseSegment}/${Date.now()}-${resourceForm.file.name}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, resourceForm.file);

        uploadTask.on('state_changed', (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          setUploadProgress(progress);
        });

        await uploadTask;
        downloadUrl = await getDownloadURL(storageRef);
        fileName = resourceForm.file.name;
        size = `${Math.round(resourceForm.file.size / 1024)} KB`;
        mimeType = resourceForm.file.type || null;
      } else {
        embedUrl = toYouTubeEmbed(resourceForm.videoUrl);
        size = 'Streaming';
      }

      const postToHomepage = isEvent && resourceForm.eventAudience === 'all';
      await addResourceFs({
        title: resourceForm.title,
        module: resourceForm.module,
        type: resourceForm.type,
        date: new Date().toISOString().slice(0, 10),
        isPaid: Boolean(resourceForm.isPaid) || resourceForm.type === 'video',
        hasWorkbook: false,
        courseId: postToHomepage ? null : (resourceForm.courseId || null),
        storagePath,
        downloadUrl,
        fileName,
        size,
        downloadable: useYoutube ? false : Boolean(resourceForm.downloadable),
        embedUrl,
        duration: resourceForm.type === 'video' ? (resourceForm.duration || 'Streaming') : null,
        eventStartDate: isEvent ? eventStartDateTime : null,
        eventEndDate: isEvent ? eventEndDateTime || null : null,
        eventLink: isEvent ? resourceForm.eventLink.trim() : null,
        mediaSource: isEvent ? resourceForm.mediaSource : null,
        postToHomepage: postToHomepage || false,
        mimeType,
        season: CURRENT_CONTENT_SEASON,
        folder: CURRENT_CONTENT_SEASON
      });

      setResourceForm({ title: '', module: '', type: 'pdf', courseId: '', file: null, downloadable: false, videoUrl: '', duration: '', isPaid: false, eventStartDate: '', eventEndDate: '', eventStartTime: '', eventEndTime: '', eventLink: '', mediaSource: 'upload', eventAudience: 'all' });
      setStatusMessage('Material uploaded and saved.');
      setUploadToast('Upload successful. Your resource is now available.');
      setTimeout(() => setUploadToast(''), 3500);
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleStartEdit = (resource) => {
    const startParts = splitDateTime(resource.eventStartDate || resource.eventDate || '');
    const endParts = splitDateTime(resource.eventEndDate || '');
    setEditingResourceId(resource.id);
    setEditingForm({
      title: resource.title || '',
      module: resource.module || '',
      type: resource.type || 'pdf',
      courseId: resource.courseId || '',
      downloadable: Boolean(resource.downloadable),
      isPaid: Boolean(resource.isPaid),
      videoUrl: resource.embedUrl || '',
      duration: resource.duration || '',
      eventStartDate: startParts.date,
      eventEndDate: endParts.date,
      eventStartTime: startParts.time,
      eventEndTime: endParts.time,
      eventLink: resource.eventLink || '',
      mediaSource: resource.mediaSource || (resource.embedUrl ? 'youtube' : 'upload'),
      eventAudience: resource.eventAudience || (resource.postToHomepage ? 'all' : 'all')
    });
    setEditStatus('');
  };

  const handleSaveCourse = async (resource) => {
    if (!useFirebase) return;
    try {
      const isVideo = editingForm.type === 'video';
      const isEvent = editingForm.type === 'event';
      const postToHomepage = isEvent && editingForm.eventAudience === 'all';
      const eventStartDateTime = combineDateTime(editingForm.eventStartDate, editingForm.eventStartTime);
      const eventEndDateTime = combineDateTime(editingForm.eventEndDate, editingForm.eventEndTime);
      if (!editingForm.title.trim() || !editingForm.module.trim()) {
        setEditStatus('Title and module are required.');
        return;
      }
      if (isEvent && !eventStartDateTime) {
        setEditStatus('Please set the event start date and time.');
        return;
      }
      if (isVideo || (isEvent && editingForm.mediaSource === 'youtube')) {
        const embed = toYouTubeEmbed(editingForm.videoUrl);
        if (!embed) {
          setEditStatus('Please enter a valid YouTube link.');
          return;
        }
        await updateResourceFs(resource.id, {
          title: editingForm.title.trim(),
          module: editingForm.module.trim(),
          type: isEvent ? 'event' : 'video',
          courseId: postToHomepage ? null : (editingForm.courseId || null),
          downloadable: false,
          isPaid: Boolean(editingForm.isPaid),
          embedUrl: embed,
          duration: isVideo ? (editingForm.duration || 'Streaming') : null,
          eventStartDate: isEvent ? eventStartDateTime || null : null,
          eventEndDate: isEvent ? eventEndDateTime || null : null,
          eventLink: isEvent ? editingForm.eventLink.trim() || null : null,
          mediaSource: isEvent ? 'youtube' : null,
          postToHomepage: postToHomepage || false
        });
      } else {
        if (!resource.storagePath) {
          setEditStatus('This resource has no file. Upload a new file to change it to a document.');
          return;
        }
        await updateResourceFs(resource.id, {
          title: editingForm.title.trim(),
          module: editingForm.module.trim(),
          type: editingForm.type,
          courseId: postToHomepage ? null : (editingForm.courseId || null),
          downloadable: Boolean(editingForm.downloadable),
          isPaid: Boolean(editingForm.isPaid),
          embedUrl: null,
          duration: editingForm.duration || null,
          eventStartDate: isEvent ? eventStartDateTime || null : null,
          eventEndDate: isEvent ? eventEndDateTime || null : null,
          eventLink: isEvent ? editingForm.eventLink.trim() || null : null,
          mediaSource: isEvent ? 'upload' : null,
          postToHomepage: postToHomepage || false
        });
      }
      setEditingResourceId('');
      setEditStatus('');
      setMoveStatus('Resource updated.');
      setTimeout(() => setMoveStatus(''), 3000);
    } catch (err) {
      console.error('Failed to update resource', err);
      setEditStatus('Unable to update resource.');
      setTimeout(() => setEditStatus(''), 3000);
    }
  };

  const handleMoveAllVideosToFamilyEconomy = async () => {
    if (!useFirebase) return;
    if (!familyEconomyCourseId) {
      setMoveStatus('Family Economy course not found.');
      setTimeout(() => setMoveStatus(''), 3000);
      return;
    }
    const videos = resources.filter((resource) => resource.type === 'video');
    if (videos.length === 0) {
      setMoveStatus('No video resources to move.');
      setTimeout(() => setMoveStatus(''), 3000);
      return;
    }
    setBulkUpdating(true);
    try {
      await Promise.all(
        videos.map((resource) =>
          updateResourceFs(resource.id, { courseId: familyEconomyCourseId, isPaid: false })
        )
      );
      setMoveStatus('All videos moved to Building a Family Economy and unlocked.');
      setTimeout(() => setMoveStatus(''), 3000);
    } catch (err) {
      console.error('Failed to move videos', err);
      setMoveStatus('Unable to move videos.');
      setTimeout(() => setMoveStatus(''), 3000);
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleDelete = async (resource) => {
    setDeletingId(resource.id);
    try {
      if (!useFirebase) return;
      if (resource.storagePath) {
        await deleteObject(ref(storage, resource.storagePath));
      }
      await removeResourceFs(resource.id);
      setHiddenIds(prev => new Set(prev).add(resource.id));
      setStatusMessage('Resource removed from storage and student portal.');
      setTimeout(() => setStatusMessage(''), 3000);
    } catch (err) {
      console.error('Failed to delete resource', err);
      setStatusMessage('Unable to remove resource. Storage delete failed.');
      setTimeout(() => setStatusMessage(''), 3000);
    } finally {
      setDeletingId('');
    }
  };

  const handleAssignmentSubmit = async () => {
    if (!useFirebase) return;
    if (!assignmentForm.title || !assignmentForm.module || !assignmentForm.dueDate) {
      setAssignmentStatus('Please complete the assignment fields.');
      setTimeout(() => setAssignmentStatus(''), 3000);
      return;
    }
    try {
      await addAssignmentFs({
        title: assignmentForm.title,
        module: assignmentForm.module,
        description: assignmentForm.description,
        dueDate: assignmentForm.dueDate,
        dueTime: assignmentForm.dueTime || '23:59',
        status: 'open',
        courseId: assignmentForm.courseId || null,
        intake: assignmentForm.intake || null,
        season: CURRENT_CONTENT_SEASON,
        attachments: [],
        details: {
          overview: assignmentForm.description
        }
      });
      setAssignmentForm({ title: '', module: '', dueDate: '', dueTime: '23:59', description: '', courseId: '', intake: '', season: CURRENT_CONTENT_SEASON });
      setAssignmentStatus('Assignment posted to students.');
      setTimeout(() => setAssignmentStatus(''), 3000);
    } catch (err) {
      console.error('Failed to create assignment', err);
      setAssignmentStatus('Unable to create assignment. Please try again.');
      setTimeout(() => setAssignmentStatus(''), 3000);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Resources</h1>
        <p className="page-subtitle">Upload and manage Season 7 student resources.</p>
      </div>

      {uploadToast && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 3000,
            background: 'var(--success)',
            color: 'white',
            padding: '14px 18px',
            borderRadius: '14px',
            boxShadow: '0 16px 32px rgba(15, 23, 42, 0.18)',
            maxWidth: '340px',
            fontWeight: 600
          }}
        >
          {uploadToast}
        </div>
      )}

      {useFirebase && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <h3 style={{ fontSize: '1.125rem' }}>Upload Resource</h3>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gap: '8px' }}>
              <div style={{ fontWeight: 600 }}>What would you like to post?</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={`btn btn-sm ${resourceForm.type === 'pdf' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setResourceForm(prev => ({ ...prev, type: 'pdf', mediaSource: 'upload' }))}
                >
                  Standard Resource
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${resourceForm.type === 'video' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setResourceForm(prev => ({ ...prev, type: 'video', file: null, mediaSource: 'youtube' }))}
                >
                  YouTube Video
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${resourceForm.type === 'event' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => setResourceForm(prev => ({ ...prev, type: 'event', mediaSource: 'upload', eventAudience: 'all', courseId: '' }))}
                >
                  Future Event Poster
                </button>
              </div>
              {resourceForm.type === 'event' && (
                <div className="badge badge-info" style={{ textAlign: 'left' }}>
                  Add an event name, start and finish times, registration link, and either a poster upload or YouTube embed.
                </div>
              )}
            </div>
            <input className="form-input" placeholder="Material title" value={resourceForm.title} onChange={(e) => setResourceForm(prev => ({ ...prev, title: e.target.value }))} />
            <input className="form-input" placeholder="Module (e.g. Module 1)" value={resourceForm.module} onChange={(e) => setResourceForm(prev => ({ ...prev, module: e.target.value }))} />
            <select
              className="form-input"
              value={resourceForm.courseId}
              onChange={(e) => setResourceForm(prev => ({ ...prev, courseId: e.target.value }))}
              disabled={resourceForm.type === 'event' && resourceForm.eventAudience === 'all'}
            >
              <option value="">All Courses (General)</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title || course.intake || course.id}
                </option>
              ))}
            </select>
            <select
              className="form-input"
              value={resourceForm.type}
              onChange={(e) => {
                const nextType = e.target.value;
                setResourceForm(prev => ({
                  ...prev,
                  type: nextType,
                  file: nextType === 'video' ? null : prev.file,
                  isPaid: nextType === 'video' ? true : prev.isPaid,
                  downloadable: nextType === 'video' ? false : prev.downloadable
                }));
              }}
            >
              <option value="pdf">PDF</option>
              <option value="video">Video</option>
              <option value="doc">Document</option>
              <option value="event">Event Poster</option>
            </select>
            {resourceForm.type === 'event' && (
              <>
                <select
                  className="form-input"
                  value={resourceForm.eventAudience}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, eventAudience: e.target.value, courseId: e.target.value === 'all' ? '' : prev.courseId }))}
                >
                  <option value="all">Post Event for all (homepage + public)</option>
                  <option value="course">Post Event for selected course</option>
                </select>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--gray-700)' }}>Starts</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '12px' }}>
                  <input
                    className="form-input"
                    type="date"
                    value={resourceForm.eventStartDate}
                    onChange={(e) => setResourceForm(prev => ({ ...prev, eventStartDate: e.target.value }))}
                    aria-label="Event start date"
                  />
                  <input
                    className="form-input"
                    type="time"
                    value={resourceForm.eventStartTime}
                    onChange={(e) => setResourceForm(prev => ({ ...prev, eventStartTime: e.target.value }))}
                    aria-label="Event start time"
                  />
                </div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--gray-700)' }}>Finishes</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '12px' }}>
                  <input
                    className="form-input"
                    type="date"
                    value={resourceForm.eventEndDate}
                    onChange={(e) => setResourceForm(prev => ({ ...prev, eventEndDate: e.target.value }))}
                    aria-label="Event finish date"
                  />
                  <input
                    className="form-input"
                    type="time"
                    value={resourceForm.eventEndTime}
                    onChange={(e) => setResourceForm(prev => ({ ...prev, eventEndTime: e.target.value }))}
                    aria-label="Event finish time"
                  />
                </div>
                <input
                  className="form-input"
                  placeholder="Event registration link"
                  value={resourceForm.eventLink}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, eventLink: e.target.value }))}
                />
                <select
                  className="form-input"
                  value={resourceForm.mediaSource}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, mediaSource: e.target.value, file: null, videoUrl: '' }))}
                >
                  <option value="upload">Upload poster / media</option>
                  <option value="youtube">YouTube embed</option>
                </select>
              </>
            )}
            {(resourceForm.type === 'video' || (resourceForm.type === 'event' && resourceForm.mediaSource === 'youtube')) && (
              <>
                <input
                  className="form-input"
                  placeholder="YouTube video link"
                  value={resourceForm.videoUrl}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, videoUrl: e.target.value }))}
                />
                <input
                  className="form-input"
                  placeholder="Duration (optional)"
                  value={resourceForm.duration}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, duration: e.target.value }))}
                />
              </>
            )}
            <select
              className="form-input"
              value={resourceForm.isPaid ? 'yes' : 'no'}
              onChange={(e) => setResourceForm(prev => ({ ...prev, isPaid: e.target.value === 'yes' }))}
            >
              <option value="yes">Premium (paid access)</option>
              <option value="no">Free access</option>
            </select>
            <select
              className="form-input"
              value={resourceForm.downloadable ? 'yes' : 'no'}
              onChange={(e) => setResourceForm(prev => ({ ...prev, downloadable: e.target.value === 'yes' }))}
            >
              <option value="no">View only (not downloadable)</option>
              <option value="yes">Allow download</option>
            </select>
            {resourceForm.type !== 'video' && !(resourceForm.type === 'event' && resourceForm.mediaSource === 'youtube') && (
              <input
                className="form-input"
                type="file"
                accept={resourceForm.type === 'event' ? 'image/*,video/*' : 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'}
                onChange={(e) => setResourceForm(prev => ({ ...prev, file: e.target.files?.[0] || null }))}
              />
            )}
            {uploading && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                Uploading… {uploadProgress}%
              </div>
            )}
            {uploadError && <div className="badge badge-danger">{uploadError}</div>}
            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
              {uploading ? `Uploading… ${uploadProgress}%` : 'Upload Resource'}
            </button>
          </div>
        </div>
      )}

      {useFirebase && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header">
            <h3 style={{ fontSize: '1.125rem' }}>Create Assignment</h3>
          </div>
          <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
            <input className="form-input" placeholder="Assignment title" value={assignmentForm.title} onChange={(e) => setAssignmentForm(prev => ({ ...prev, title: e.target.value }))} />
            <input className="form-input" value={CURRENT_CONTENT_SEASON} readOnly aria-label="Content season" />
            <input className="form-input" placeholder="Module (e.g. Module 1)" value={assignmentForm.module} onChange={(e) => setAssignmentForm(prev => ({ ...prev, module: e.target.value }))} />
            <textarea className="form-input form-textarea" placeholder="Assignment instructions" value={assignmentForm.description} onChange={(e) => setAssignmentForm(prev => ({ ...prev, description: e.target.value }))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <input className="form-input" type="date" value={assignmentForm.dueDate} onChange={(e) => setAssignmentForm(prev => ({ ...prev, dueDate: e.target.value }))} />
              <input className="form-input" type="time" value={assignmentForm.dueTime} onChange={(e) => setAssignmentForm(prev => ({ ...prev, dueTime: e.target.value }))} />
            </div>
            <select
              className="form-input"
              value={assignmentForm.courseId}
              onChange={(e) => setAssignmentForm(prev => ({ ...prev, courseId: e.target.value }))}
            >
              <option value="">All Courses (General)</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title || course.intake || course.id}
                </option>
              ))}
            </select>
            <input className="form-input" placeholder="Intake label (optional)" value={assignmentForm.intake} onChange={(e) => setAssignmentForm(prev => ({ ...prev, intake: e.target.value }))} />
            {assignmentStatus && <div className="badge badge-info">{assignmentStatus}</div>}
            <button className="btn btn-primary" onClick={handleAssignmentSubmit}>
              Post Assignment
            </button>
            <button className="btn btn-outline" onClick={handleExtendAllAssignments} disabled={bulkUpdating}>
              {bulkUpdating ? 'Updating…' : 'Extend all assignments to Friday'}
            </button>
          </div>
        </div>
      )}

      {statusMessage && (
        <div className="badge badge-success" style={{ marginBottom: '16px' }}>
          {statusMessage}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: '1.125rem' }}>Uploaded Resources</h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button className="btn btn-outline btn-sm" onClick={handleMoveAllVideosToFamilyEconomy} disabled={bulkUpdating}>
                {bulkUpdating ? 'Moving…' : 'Move all videos to Building a Family Economy'}
              </button>
            </div>
          </div>
        </div>
        <div className="card-body" style={{ display: 'grid', gap: '12px' }}>
          {assignmentStatus && (
            <div className="badge badge-info">{assignmentStatus}</div>
          )}
          {moveStatus && (
            <div className="badge badge-info">{moveStatus}</div>
          )}
          {sortedResources.length === 0 && (
            <div style={{ color: 'var(--gray-500)' }}>No resources uploaded yet.</div>
          )}
          {sortedResources.map((resource) => (
            <div key={resource.id} style={{ padding: '12px', borderRadius: '12px', border: '1px solid var(--gray-100)', background: 'var(--gray-50)', display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{resource.title}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{resource.module}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--gold-dark)', fontWeight: 600 }}>{getResourceFolder(resource)}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)' }}>{resource.fileName || resource.storagePath}</div>
                {resource.type === 'event' && formatEventWindow(resource.eventStartDate || resource.eventDate, resource.eventEndDate) && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '6px' }}>
                    {formatEventWindow(resource.eventStartDate || resource.eventDate, resource.eventEndDate)}
                  </div>
                )}
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '6px' }}>
                  Course: {courses.find(course => course.id === resource.courseId)?.title || 'General'}
                </div>
                {editingResourceId === resource.id && (
                  <div style={{ marginTop: '8px', display: 'grid', gap: '8px', maxWidth: '320px' }}>
                    <input
                      className="form-input"
                      placeholder="Title"
                      value={editingForm.title}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, title: e.target.value }))}
                    />
                    <input
                      className="form-input"
                      placeholder="Module"
                      value={editingForm.module}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, module: e.target.value }))}
                    />
                    <select
                      className="form-input"
                      value={editingForm.courseId}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, courseId: e.target.value }))}
                      disabled={editingForm.type === 'event' && editingForm.eventAudience === 'all'}
                    >
                      <option value="">All Courses (General)</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.title || course.intake || course.id}
                        </option>
                      ))}
                    </select>
                    <select
                      className="form-input"
                      value={editingForm.type}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option value="pdf">PDF</option>
                      <option value="video">Video</option>
                      <option value="doc">Document</option>
                      <option value="event">Event Poster</option>
                    </select>
                    {editingForm.type === 'event' && (
                      <>
                        <select
                          className="form-input"
                          value={editingForm.eventAudience}
                          onChange={(e) => setEditingForm(prev => ({ ...prev, eventAudience: e.target.value, courseId: e.target.value === 'all' ? '' : prev.courseId }))}
                        >
                          <option value="all">Post Event for all (homepage + public)</option>
                          <option value="course">Post Event for selected course</option>
                        </select>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--gray-700)' }}>Starts</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '8px' }}>
                          <input
                            className="form-input"
                            type="date"
                            value={editingForm.eventStartDate}
                            onChange={(e) => setEditingForm(prev => ({ ...prev, eventStartDate: e.target.value }))}
                            aria-label="Event start date"
                          />
                          <input
                            className="form-input"
                            type="time"
                            value={editingForm.eventStartTime}
                            onChange={(e) => setEditingForm(prev => ({ ...prev, eventStartTime: e.target.value }))}
                            aria-label="Event start time"
                          />
                        </div>
                        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--gray-700)' }}>Finishes</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: '8px' }}>
                          <input
                            className="form-input"
                            type="date"
                            value={editingForm.eventEndDate}
                            onChange={(e) => setEditingForm(prev => ({ ...prev, eventEndDate: e.target.value }))}
                            aria-label="Event finish date"
                          />
                          <input
                            className="form-input"
                            type="time"
                            value={editingForm.eventEndTime}
                            onChange={(e) => setEditingForm(prev => ({ ...prev, eventEndTime: e.target.value }))}
                            aria-label="Event finish time"
                          />
                        </div>
                        <input
                          className="form-input"
                          placeholder="Event registration link"
                          value={editingForm.eventLink}
                          onChange={(e) => setEditingForm(prev => ({ ...prev, eventLink: e.target.value }))}
                        />
                        <select
                          className="form-input"
                          value={editingForm.mediaSource}
                          onChange={(e) => setEditingForm(prev => ({ ...prev, mediaSource: e.target.value }))}
                        >
                          <option value="upload">Uploaded poster / media</option>
                          <option value="youtube">YouTube embed</option>
                        </select>
                      </>
                    )}
                    {(editingForm.type === 'video' || (editingForm.type === 'event' && editingForm.mediaSource === 'youtube')) && (
                      <>
                        <input
                          className="form-input"
                          placeholder="YouTube video link"
                          value={editingForm.videoUrl}
                          onChange={(e) => setEditingForm(prev => ({ ...prev, videoUrl: e.target.value }))}
                        />
                        <input
                          className="form-input"
                          placeholder="Duration (optional)"
                          value={editingForm.duration}
                          onChange={(e) => setEditingForm(prev => ({ ...prev, duration: e.target.value }))}
                        />
                      </>
                    )}
                    <select
                      className="form-input"
                      value={editingForm.isPaid ? 'yes' : 'no'}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, isPaid: e.target.value === 'yes' }))}
                    >
                      <option value="yes">Premium (paid access)</option>
                      <option value="no">Free access</option>
                    </select>
                    <select
                      className="form-input"
                      value={editingForm.downloadable ? 'yes' : 'no'}
                      onChange={(e) => setEditingForm(prev => ({ ...prev, downloadable: e.target.value === 'yes' }))}
                      disabled={editingForm.type === 'video'}
                    >
                      <option value="no">View only (not downloadable)</option>
                      <option value="yes">Allow download</option>
                    </select>
                    {editStatus && <div className="badge badge-danger">{editStatus}</div>}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => handleSaveCourse(resource)}>Save</button>
                      <button className="btn btn-outline btn-sm" onClick={() => { setEditingResourceId(''); setEditStatus(''); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => handleStartEdit(resource)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => handleDelete(resource)}
                  disabled={deletingId === resource.id}
                >
                  {deletingId === resource.id ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
